import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Building2, Check, LoaderCircle, Search, Store, X } from 'lucide-react';
import { GlobalContext } from '../context/GlobalContext';
import { callAPI } from '../hooks/api';
import { Button } from '../shadcn/Button';
import { SellerCommissionTarget } from '../types/seller-commissions';

const TARGET_BATCH_SIZE = 30;

export default function SellerCommissionTargetSelector({
	country,
	selectedKeys,
	onSelect
}: {
	country: string;
	selectedKeys: string[];
	onSelect: (target: SellerCommissionTarget) => void;
}) {
	const [master, setMaster] = useState<SellerCommissionTarget | null>(null);
	const masterSelected = !!master && selectedKeys.includes(master.key);

	useEffect(() => setMaster(null), [country]);

	const addMaster = () => {
		if (!master || masterSelected) return;
		onSelect(master);
	};

	return (
		<div className="space-y-3">
			{master ? (
				<>
					<div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-neutral-800 bg-neutral-925/50 p-3">
						<div className="flex min-w-0 items-center gap-3">
							<div className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-neutral-900 text-primary-300">
								<Store className="size-4" />
							</div>
							<div className="min-w-0">
								<p className="truncate text-sm font-medium text-neutral-100">{master.master_store_name}</p>
								<p className="truncate text-xs text-neutral-500">{master.master_store_email}</p>
							</div>
						</div>
						<div className="flex gap-2">
							<Button variant="secondary" className="!w-auto" onClick={() => setMaster(null)}>
								<X className="size-4" /> Cambiar
							</Button>
							<Button variant="primary" className="!w-auto" disabled={masterSelected} onClick={addMaster}>
								{masterSelected ? <Check className="size-4" /> : null}
								{masterSelected ? 'Tienda completa agregada' : 'Agregar tienda completa'}
							</Button>
						</div>
					</div>
					<div>
						{masterSelected ? (
							<p className="rounded-lg border border-primary-500/20 bg-primary-500/5 p-3 text-xs text-primary-300">
								La tienda completa ya incluye todas sus sucursales.
							</p>
						) : (
							<>
								<p className="mb-2 text-xs text-neutral-500">O agrega una sucursal específica de esta tienda.</p>
								<TargetSearchList
									country={country}
									masterStoreEmail={master.master_store_email}
									selectedKeys={selectedKeys}
									placeholder="Buscar sucursal por nombre o email..."
									emptyMessage="Esta tienda no tiene sucursales disponibles."
									onInitialEmpty={addMaster}
									onSelect={onSelect}
								/>
							</>
						)}
					</div>
				</>
			) : (
				<TargetSearchList
					country={country}
					selectedKeys={selectedKeys}
					placeholder="Buscar tienda master por nombre o email..."
					emptyMessage="No hay tiendas master disponibles."
					onSelect={setMaster}
				/>
			)}
		</div>
	);
}

function TargetSearchList({
	country,
	masterStoreEmail,
	selectedKeys,
	placeholder,
	emptyMessage,
	onInitialEmpty,
	onSelect
}: {
	country: string;
	masterStoreEmail?: string;
	selectedKeys: string[];
	placeholder: string;
	emptyMessage: string;
	onInitialEmpty?: () => void;
	onSelect: (target: SellerCommissionTarget) => void;
}) {
	const { userData } = useContext(GlobalContext);
	const [query, setQuery] = useState('');
	const [debouncedQuery, setDebouncedQuery] = useState('');
	const [targets, setTargets] = useState<SellerCommissionTarget[]>([]);
	const [nextCursor, setNextCursor] = useState<string | null>(null);
	const [hasMore, setHasMore] = useState(false);
	const [loading, setLoading] = useState(true);
	const [loadingMore, setLoadingMore] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [reload, setReload] = useState(0);
	const listRef = useRef<HTMLDivElement | null>(null);
	const sentinelRef = useRef<HTMLDivElement | null>(null);
	const requestScopeRef = useRef('');
	const onInitialEmptyRef = useRef(onInitialEmpty);

	useEffect(() => {
		onInitialEmptyRef.current = onInitialEmpty;
	}, [onInitialEmpty]);

	useEffect(() => {
		const timeout = window.setTimeout(() => setDebouncedQuery(query.trim()), 300);
		return () => window.clearTimeout(timeout);
	}, [query]);

	useEffect(() => {
		if (!userData) return;
		const controller = new AbortController();
		const requestScope = `${country}|${masterStoreEmail || ''}|${debouncedQuery}|${reload}`;
		requestScopeRef.current = requestScope;
		setLoading(true);
		setLoadingMore(false);
		setError(null);
		setTargets([]);
		setNextCursor(null);
		setHasMore(false);

		void callAPI(
			'https://api.krece.app/storeprofile/seller_commissions/targets/',
			'GET',
			{
				country,
				batch_size: TARGET_BATCH_SIZE,
				...(debouncedQuery && { search: debouncedQuery }),
				...(masterStoreEmail && { master_store_email: masterStoreEmail })
			},
			userData,
			country,
			true,
			false,
			controller.signal
		).then(([response, requestError]) => {
			if (controller.signal.aborted || requestScopeRef.current !== requestScope) return;
			setLoading(false);
			if (requestError || !response) {
				setError(requestError || 'No se pudo cargar el alcance.');
				return;
			}
			setTargets(response.targets);
			setNextCursor(response.next_cursor);
			setHasMore(response.has_more);
			if (!debouncedQuery && response.targets.length === 0 && !response.has_more) onInitialEmptyRef.current?.();
		});

		return () => {
			controller.abort();
			if (requestScopeRef.current === requestScope) requestScopeRef.current = '';
		};
	}, [country, debouncedQuery, masterStoreEmail, reload, userData]);

	const loadMore = useCallback(async () => {
		if (!userData || !nextCursor || loading || loadingMore || !hasMore) return;
		const requestScope = requestScopeRef.current;
		setLoadingMore(true);
		setError(null);
		const [response, requestError] = await callAPI(
			'https://api.krece.app/storeprofile/seller_commissions/targets/',
			'GET',
			{
				country,
				batch_size: TARGET_BATCH_SIZE,
				cursor: nextCursor,
				...(debouncedQuery && { search: debouncedQuery }),
				...(masterStoreEmail && { master_store_email: masterStoreEmail })
			},
			userData,
			country,
			true,
			false
		);
		if (requestScopeRef.current !== requestScope) return;
		setLoadingMore(false);
		if (requestError || !response) {
			setError(requestError || 'No se pudo cargar más alcance.');
			return;
		}
		setTargets((current) => {
			const seen = new Set(current.map((target) => target.key));
			return [...current, ...response.targets.filter((target) => !seen.has(target.key))];
		});
		setNextCursor(response.next_cursor);
		setHasMore(response.has_more);
	}, [country, debouncedQuery, hasMore, loading, loadingMore, masterStoreEmail, nextCursor, userData]);

	useEffect(() => {
		const sentinel = sentinelRef.current;
		const root = listRef.current;
		if (!sentinel || !root || !hasMore || loading || loadingMore) return;
		const observer = new IntersectionObserver(
			(entries) => {
				if (entries[0]?.isIntersecting) void loadMore();
			},
			{ root, rootMargin: '80px' }
		);
		observer.observe(sentinel);
		return () => observer.disconnect();
	}, [hasMore, loadMore, loading, loadingMore]);

	const selected = new Set(selectedKeys);

	return (
		<div className="overflow-hidden rounded-lg border border-neutral-800 bg-neutral-925/50 focus-within:border-primary-500/50">
			<label className="flex h-11 items-center gap-2 border-b border-neutral-800 px-3">
				<Search className="size-4 shrink-0 text-neutral-500" />
				<input
					value={query}
					onChange={(event) => setQuery(event.target.value)}
					placeholder={placeholder}
					className="h-full w-full bg-transparent text-sm text-neutral-200 outline-none placeholder:text-neutral-600"
				/>
			</label>
			<div ref={listRef} className="max-h-56 overflow-y-auto overscroll-contain">
				{loading ? (
					<div className="flex items-center justify-center gap-2 px-3 py-6 text-sm text-neutral-500">
						<LoaderCircle className="size-4 animate-spin" /> Cargando alcance...
					</div>
				) : error && !targets.length ? (
					<div className="flex items-center justify-between gap-3 px-3 py-4 text-sm text-red-300">
						<span>{error}</span>
						<Button variant="secondary" className="!w-auto" onClick={() => setReload((value) => value + 1)}>
							Reintentar
						</Button>
					</div>
				) : targets.length || hasMore ? (
					<>
						{targets.map((target) => {
							const isSelected = selected.has(target.key);
							return (
								<button
									key={target.key}
									type="button"
									disabled={isSelected}
									onClick={() => onSelect(target)}
									className="flex w-full items-center gap-3 border-b border-neutral-800 px-3 py-2.5 text-left last:border-b-0 hover:bg-neutral-900/70 focus:bg-neutral-900/70 focus:outline-none disabled:bg-primary-500/5"
								>
									{target.type === 'master' ? (
										<Store className="size-4 shrink-0 text-primary-300" />
									) : (
										<Building2 className="size-4 shrink-0 text-primary-300" />
									)}
									<span className="min-w-0">
										<span className="block truncate text-sm text-neutral-100">
											{target.type === 'master' ? target.master_store_name : target.branch_store_name}
										</span>
										<span className="block truncate text-xs text-neutral-500">
											{target.type === 'master' ? target.master_store_email : target.branch_store_email}
										</span>
									</span>
									{isSelected && <Check className="ml-auto size-4 shrink-0 text-primary-300" />}
								</button>
							);
						})}
						{error ? (
							<div className="flex items-center justify-between gap-3 px-3 py-3 text-xs text-red-300">
								<span>{error}</span>
								<Button variant="secondary" className="!w-auto" onClick={() => void loadMore()}>
									Reintentar
								</Button>
							</div>
						) : (
							<div ref={sentinelRef} className="flex h-8 items-center justify-center text-neutral-500">
								{loadingMore && <LoaderCircle className="size-4 animate-spin" />}
							</div>
						)}
					</>
				) : (
					<div className="px-3 py-6 text-center text-sm text-neutral-500">{emptyMessage}</div>
				)}
			</div>
		</div>
	);
}
